import { useCallback, useEffect, useState } from 'react';
import type { UploadedFileSummary } from '../types';
import { deleteUploadedFile, listConversationFiles } from '../services/uploadedFiles';

/** How long after a turn is sent the server has taken it, and lists what it brought. */
const REREAD_AFTER_SEND_MS = 5000;

/**
 * The files a conversation holds, as the server lists them.
 *
 * Read when the conversation opens, when a turn is sent and when it ends: a
 * turn can add to them. Removing one asks the server to, and reads again.
 */
export function useConversationFiles(sessionId: string | undefined, turnRunning: boolean) {
  const [files, setFiles] = useState<UploadedFileSummary[]>([]);
  const [rereads, setRereads] = useState(0);
  const [removeFailed, setRemoveFailed] = useState(false);

  // Another conversation: nothing of the one left behind stays.
  useEffect(() => {
    setFiles([]);
    setRemoveFailed(false);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let current = true;
    listConversationFiles(sessionId)
      .then(held => {
        if (current) setFiles(held);
      })
      // Not listed is not "holds none": what is shown stays as it was.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [sessionId, turnRunning, rereads]);

  // The server lists what a turn brought once it has taken the turn, a moment
  // after the turn is sent. The read made as it is sent comes back with the
  // list as it was, and the next one would be at the turn's end, minutes on.
  useEffect(() => {
    if (!sessionId || !turnRunning) return undefined;
    const timer = setTimeout(() => setRereads(count => count + 1), REREAD_AFTER_SEND_MS);
    return () => clearTimeout(timer);
  }, [sessionId, turnRunning]);

  const remove = useCallback(async (file: UploadedFileSummary) => {
    setRemoveFailed(false);
    try {
      await deleteUploadedFile(file.fileId || file.localId);
      setRereads(count => count + 1);
    } catch {
      setRemoveFailed(true);
    }
  }, []);

  return { files, remove, removeFailed };
}
